
import { Heart, Mail, Phone, MapPin } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSiteLogo } from "@/hooks/useSiteLogo";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { siteLogo, loading: logoLoading } = useSiteLogo();
  const [contactInfo, setContactInfo] = useState({
    email: 'support@school-system.com',
    phone: '+966 50 123 4567',
    address: 'المملكة العربية السعودية'
  });
  // روابط ثابتة للفوتر
  const footerLinks = [
    { title: "الشروط والأحكام", url: "/terms", external: false },
    { title: "سياسة الخصوصية", url: "/privacy", external: false },
    { title: "تواصل معنا", url: "/contact", external: false },
    { title: "المساعدة", url: "/help", external: false }
  ];

  useEffect(() => {
    fetchSiteContent();
    // النسخة الاحتياطية من localStorage
    const savedContent = localStorage.getItem('siteContent');
    if (savedContent) {
      const data = JSON.parse(savedContent);
      setContactInfo({
        email: data.contact_email || 'support@school-system.com',
        phone: data.contact_phone || '+966 50 123 4567',
        address: data.contact_address || 'المملكة العربية السعودية'
      });
    }
  }, []);

  const fetchSiteContent = async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .eq('content_type', 'contact_info');
      
      if (error) {
        console.error('Error fetching site content:', error);
        return;
      }

      data?.forEach(item => {
        if (item.content_type === 'contact_info' && item.content && typeof item.content === 'object' && !Array.isArray(item.content)) {
          setContactInfo(item.content as any);
        }
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <footer className="bg-school-navy text-white" dir="rtl">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {siteLogo?.url && !logoLoading ? (
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center p-1">
                  <img 
                    src={siteLogo.url} 
                    alt={siteLogo.alt_text || 'شعار الموقع'} 
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-8 h-8 bg-school-green rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">ح</span>
                </div>
              )}
              <h3 className="text-xl font-bold text-school-green">منصة حاضرون التعليمية</h3>
            </div>
            <p className="text-school-gray leading-relaxed">
              منصة شاملة ومتقدمة لرصد الحضور اليومي للطلاب مع الإحصائيات المتقدمة والتقارير التفصيلية.
            </p>
            <div className="flex items-center gap-2 text-school-teal">
              <Heart className="h-4 w-4" />
              <span className="text-sm">صُنع بحب للتعليم</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-school-green">روابط سريعة</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-school-gray hover:text-school-teal transition-colors">الصفحة الرئيسية</Link></li>
              {/* <li><Link to="/students" className="text-school-gray hover:text-school-teal transition-colors">إدارة الطلاب</Link></li>
              <li><Link to="/attendance" className="text-school-gray hover:text-school-teal transition-colors">تسجيل الحضور</Link></li>
              <li><Link to="/reports" className="text-school-gray hover:text-school-teal transition-colors">التقارير والإحصائيات</Link></li>
              <li><Link to="/school-settings" className="text-school-gray hover:text-school-teal transition-colors">إعدادات المدرسة</Link></li> */}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-school-green">معلومات التواصل</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-school-gray">
                <Mail className="h-4 w-4 text-school-teal" />
                <span>{contactInfo.email}</span>
              </div>
              <div className="flex items-center gap-3 text-school-gray">
                <Phone className="h-4 w-4 text-school-teal" />
                <span>{contactInfo.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-school-gray">
                <MapPin className="h-4 w-4 text-school-teal" />
                <span>{contactInfo.address}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-school-blue/20 mt-8 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-school-gray text-sm">
              © {currentYear} منصة حاضرون التعليمية. جميع الحقوق محفوظة.
            </p>
            <div className="flex items-center gap-4 text-sm text-school-gray">
              {footerLinks.slice(0, 3).map((link, index) => (
                <div key={index} className="flex items-center gap-4">
                  {index > 0 && <span>|</span>}
                  <Link 
                    to={link.url} 
                    className="hover:text-school-teal transition-colors"
                    target={link.external ? "_blank" : undefined}
                    rel={link.external ? "noopener noreferrer" : undefined}
                  >
                    {link.title}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
